import { useState } from 'react';
import { Package, Cpu, Type, CreditCard, UserCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import CatalogTab from '../components/settings/CatalogTab';
import MachinesTab from '../components/settings/MachinesTab';
import FontsTab from '../components/settings/FontsTab';
import SubscriptionTab from '../components/settings/SubscriptionTab';
import AccountTab from '../components/settings/AccountTab';

type TabId = 'catalog' | 'machines' | 'fonts' | 'subscription' | 'account';

interface TabItem {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

const tabs: TabItem[] = [
  { id: 'catalog', label: 'Products', icon: <Package size={18} /> },
  { id: 'machines', label: 'Machines', icon: <Cpu size={18} /> },
  { id: 'fonts', label: 'Fonts', icon: <Type size={18} /> },
  { id: 'subscription', label: 'Subscription', icon: <CreditCard size={18} /> },
  { id: 'account', label: 'Account', icon: <UserCircle size={18} /> },
];

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('catalog');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const renderTab = () => {
    switch (activeTab) {
      case 'catalog': return <CatalogTab />;
      case 'machines': return <MachinesTab />;
      case 'fonts': return <FontsTab />;
      case 'subscription': return <SubscriptionTab />;
      case 'account': return <AccountTab />;
    }
  };

  return (
    <div className="flex h-full">
      {/* Settings Sidebar */}
      <div
        className={`flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex flex-col transition-all duration-200 ${
          sidebarCollapsed ? 'w-16' : 'w-56'
        }`}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          {!sidebarCollapsed && (
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Settings
            </h2>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-400 transition"
          >
            {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 p-2 space-y-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title={sidebarCollapsed ? tab.label : undefined}
            >
              <span className="flex-shrink-0">{tab.icon}</span>
              {!sidebarCollapsed && <span>{tab.label}</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-5xl">
          {renderTab()}
        </div>
      </div>
    </div>
  );
}
